import React, { useCallback, useEffect, useState } from "react";
import { Button, Form, Input, Result, Row } from "antd";
import { useNavigate } from "react-router-dom";
import { AlertType, useAlert } from "../provider/AlertProvider";
import { useAuthDispatch } from "../provider/AuthProvider";
import { useAsyncAxios, utilAxiosWithAppToken } from "../utils/customAxios";

import * as TypeDTO from "../commons/typeDTO";
import * as Request from "../commons/request";
import * as String from "../commons/string";
import * as Common from "../commons/common";
import * as Utils from "../utils/utils";
import PhoneCertification from "../components/PhoneCertification";
import InputPassword from "../components/InputPassword";
import PageContainer from "../layout/PageContainer";

import styles from "./Login.module.css";

function ChangePassword() {
    const alert = useAlert();
    const dispatch = useAuthDispatch();
    const navigate = useNavigate();

    const [form] = Form.useForm();
    const [userName, setUserName] = useState("");
    const [certificationResult, setCertificationResult] = useState(false);
    const [phoneNumber, setPhoneNumber] = useState("");
    const [result, setResult] = useState("");
    const [newPasswordReady, setNewPasswordReady] = useState<boolean>(true);
    const [disableInputName, setDisableInputName] = useState(false);

    const [email, setEmail] = useState<string>("");

    const requestResetPassword = async (url: string, option: Record<string, string>) => {
        const response = await utilAxiosWithAppToken().post(url, option);
        return response.data;
    };

    const { error: errorResetPassword, data: resultResetPassword, execute: resetPassword } = useAsyncAxios(requestResetPassword);

    useEffect(() => {
        if (!resultResetPassword) return;

        dispatch && dispatch({ type: "LOGOUT" });

        navigate(Common.PAGE_LOGIN, { replace: true });
        alert.setAlert(AlertType.SUCCESS, "비밀번호 재설정 성공", "비밀번호를 재설정하였습니다.");
    }, [resultResetPassword]);

    useEffect(() => {
        if (!errorResetPassword) return;

        alert.setAlert(errorResetPassword.response?.data?.code, "비밀번호 재설정 실패", errorResetPassword.response?.data?.message);
    }, [errorResetPassword]);

    const actionResetPassword = async (data: TypeDTO.ResetPasswordDto) => {
        await resetPassword(Request.RESET_PASSWORD_URL, data);
    };

    const onFinish = (values: any) => {
        delete values["confirm"];
        delete values["name"];
        const data: TypeDTO.ResetPasswordDto = {
            ...values,
            phone: phoneNumber,
        };

        actionResetPassword(data);
    };

    const handleCertificationResult = (success: boolean, phoneNumber: string) => {
        setPhoneNumber(phoneNumber);
        setCertificationResult(success);

        setDisableInputName(success);
    };

    const validatePwd = useCallback((_: any, value: string) => {
        const pwd_regExp = Common.PASSWORD_REGEXP;

        if (!pwd_regExp.test(value)) {
            return Promise.reject(new Error(String.msg_pwd_rule));
        }
        return Promise.resolve();
    }, []);

    useEffect(() => {
        if (email.includes(" ")) {
            const newEmail = email.replace(/\s/g, "");
            form.setFieldsValue({
                email: newEmail,
            });
            setEmail(newEmail);
        }
    }, [email]);

    return (
        <PageContainer pageBackground={`#fff`} contentPadding={16}>
            <Row align="middle" justify="center">
                <div className={`${styles.loginCard} container`}>
                    <Form form={form} onFinish={onFinish} layout="vertical">
                        <Form.Item
                            className={styles.textInputLabel}
                            name="email"
                            label={String.email}
                            rules={[
                                {
                                    required: true,
                                    message: "이메일을 입력해 주세요.",
                                },
                                {
                                    validator: (_, value) => Utils.validateEmail(true, true, value),
                                },
                            ]}
                        >
                            <Input
                                className={styles.loginInput}
                                placeholder={String.email}
                                value={email}
                                onInput={(e) => ((e.target as HTMLInputElement).value = (e.target as HTMLInputElement).value.toLowerCase())}
                                onChange={(e) => setEmail(e.target.value)}
                            />
                        </Form.Item>
                        <Form.Item
                            className={styles.textInputLabel}
                            name="name"
                            label={String.name}
                            initialValue={userName}
                            rules={[
                                {
                                    required: true,
                                    message: "이름을 입력해 주세요.",
                                },
                            ]}
                        >
                            <Input
                                className={styles.loginInput}
                                placeholder="휴대전화 명의자 이름을 입력해 주세요."
                                disabled={disableInputName}
                                onChange={(e) => setUserName(e.target.value)}
                            />
                        </Form.Item>

                        <Form.Item
                            className={styles.textInputLabel}
                            name="phone"
                            label={String.phone}
                            required={true}
                            rules={[
                                () => ({
                                    validator(_, value) {
                                        if (certificationResult) {
                                            return Promise.resolve();
                                        }

                                        return Promise.reject(new Error("휴대전화 본인 인증을 해 주세요."));
                                    },
                                }),
                            ]}
                        >
                            <PhoneCertification userName={userName} onChangedResult={handleCertificationResult} />
                        </Form.Item>

                        {certificationResult && (
                            <>
                                <Form.Item
                                    className={styles.textInputLabel}
                                    name="password"
                                    label={String.password}
                                    rules={[
                                        {
                                            required: true,
                                            message: String.msg_pwd_must,
                                        },
                                        { validator: validatePwd },
                                    ]}
                                    hasFeedback
                                >
                                    <InputPassword />
                                </Form.Item>

                                <Form.Item
                                    className={styles.textInputLabel}
                                    name="confirm"
                                    label={String.passwordConfirm}
                                    dependencies={["password"]}
                                    hasFeedback
                                    rules={[
                                        {
                                            required: true,
                                            message: "비밀번호를 확인해 주세요.",
                                        },
                                        ({ getFieldValue }) => ({
                                            validator(_, value) {
                                                if (!value || getFieldValue("password") === value) {
                                                    setNewPasswordReady(false);
                                                    return Promise.resolve();
                                                }

                                                return Promise.reject(new Error("입력한 비밀번호가 일치하지 않습니다."));
                                            },
                                        }),
                                    ]}
                                >
                                    <InputPassword />
                                </Form.Item>
                            </>
                        )}
                        <Form.Item className={styles.loginBtnWrapper}>
                            <Button className={styles.loginBtn} block type="primary" htmlType="submit" size="large" disabled={newPasswordReady}>
                                {String.reset}
                            </Button>
                        </Form.Item>
                    </Form>
                    {result.length > 0 && <Result status="success" title={result} />}
                </div>
            </Row>
        </PageContainer>
    );
}

export default ChangePassword;
